import { Box, ButtonBase, Menu, MenuItem } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState } from "react";

import type { SupportStatus } from "../../types.js";

/**
 * Click-to-change status pill. Renders the current status as a
 * coloured chip (the same `color` token the StatusBadge uses) plus a
 * chevron — clicking opens a dropdown of every defined status.
 *
 * Used twice on the ticket page:
 *   * In the title row, where the status is the canonical place to
 *     change a ticket's state (Linear / Jira pattern).
 *   * Anywhere else a status change is contextually appropriate.
 *
 * The current status stays selectable in the dropdown — clicking it
 * is a no-op on the server but lets the user "confirm" it without
 * losing the menu.
 */
export function TransitionMenu({
  current,
  options,
  onTransition,
  isPending,
}: {
  current: { id: number; name: string; color?: string | null };
  options: SupportStatus[];
  onTransition: (statusId: number) => void;
  isPending: boolean;
}): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <Box sx={{ display: "inline-flex" }}>
      <ButtonBase
        onClick={(e) => setAnchorEl(e.currentTarget)}
        disabled={isPending}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Change status"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.75,
          borderRadius: 999,
          border: 1,
          borderColor: "divider",
          bgcolor: "background.default",
          px: 1.5,
          py: 0.5,
          fontSize: 12,
          fontWeight: 500,
          color: "text.primary",
          "&:hover": { bgcolor: "surfaceSoft" },
          "&.Mui-disabled": { opacity: 0.5 },
        }}
      >
        <Box
          component="span"
          sx={{ height: 6, width: 6, borderRadius: "50%", bgcolor: "text.disabled" }}
          style={current.color ? { backgroundColor: current.color } : undefined}
        />
        {current.name}
        <Box component="svg" viewBox="0 0 8 8" aria-hidden sx={{ height: 8, width: 8, opacity: 0.7 }}>
          <path d="M1 3l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </Box>
      </ButtonBase>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        MenuListProps={{ role: "menu", dense: true, sx: { minWidth: 200 } }}
      >
        {options.map((s) => {
          const isCurrent = s.id === current.id;
          return (
            <MenuItem
              key={s.id}
              role="menuitem"
              onClick={() => {
                setAnchorEl(null);
                if (!isCurrent) onTransition(s.id);
              }}
              sx={[
                { gap: 1, fontSize: 12 },
                isCurrent
                  ? {
                      bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                      color: "primary.main",
                    }
                  : { color: "text.primary" },
              ]}
            >
              <Box
                component="span"
                sx={{ height: 6, width: 6, borderRadius: "50%", bgcolor: "text.disabled" }}
                style={s.color ? { backgroundColor: s.color } : undefined}
              />
              {s.name}
              {isCurrent && (
                <Box
                  component="span"
                  sx={{ ml: "auto", fontSize: 10, textTransform: "uppercase", letterSpacing: "0.05em", opacity: 0.7 }}
                >
                  current
                </Box>
              )}
            </MenuItem>
          );
        })}
        {options.length === 0 && (
          <MenuItem disabled sx={{ fontSize: 12, color: "text.disabled" }}>
            No transitions available.
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
